/**
 * Welcome Store Cart Restrictions
 *
 * Enforces cart limits for welcome store collections:
 * - Only 1 welcome item allowed in cart
 * - Quantity locked at 1 for welcome items
 * - Cannot mix marine and aviation welcome items
 *
 * Requires: #welcome-cart-config JSON block in theme.liquid
 * Tags: welcome-store-item, welcome-marine, welcome-aviation
 */
(function () {
  'use strict';

  var configEl = document.getElementById('welcome-cart-config');
  if (!configEl) return;

  var config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (err) {
    return;
  }

  var originalFetch = window.fetch;
  var uiTimeout = null;
  var welcomeKeysInCart = null; // null = not yet synced, {} = synced (empty or populated)

  /**
   * Fetch current cart state from Shopify
   */
  function getCart() {
    return originalFetch('/cart.js')
      .then(function (res) { return res.json(); })
      .catch(function () { return null; });
  }

  /**
   * Find a welcome item in the cart by checking line item properties
   */
  function findWelcomeInCart(cart) {
    if (!cart || !cart.items) return null;

    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (item.properties && item.properties[config.props.item]) {
        return {
          type: item.properties[config.props.type] || '',
          key: item.key
        };
      }
    }
    return null;
  }

  /**
   * Display an error notification to the user
   */
  function showError(msg) {
    var el = document.getElementById('welcome-notify');

    if (!el) {
      el = document.createElement('div');
      el.id = 'welcome-notify';
      el.setAttribute('role', 'alert');
      el.setAttribute('aria-live', 'assertive');
      el.style.cssText = [
        'position:fixed',
        'top:20px',
        'left:50%',
        'transform:translateX(-50%)',
        'z-index:99999',
        'padding:16px 24px',
        'border-radius:4px',
        'font-size:14px',
        'max-width:90%',
        'text-align:center',
        'box-shadow:0 4px 12px rgba(0,0,0,.15)',
        'background:#dc3545',
        'color:#fff',
        'transition:opacity .3s',
        'pointer-events:none'
      ].join(';');
      document.body.appendChild(el);
    }

    el.textContent = msg;
    el.style.opacity = '1';

    setTimeout(function () {
      el.style.opacity = '0';
    }, 5000);
  }

  /**
   * Parse request body into a plain object (for reading values)
   */
  function parseBody(body) {
    if (!body) return null;

    if (typeof body === 'string') {
      try {
        return JSON.parse(body);
      } catch (err) {
        var obj = {};
        new URLSearchParams(body).forEach(function (v, k) { obj[k] = v; });
        return obj;
      }
    }

    if (body instanceof FormData) {
      var formObj = {};
      body.forEach(function (v, k) { formObj[k] = v; });
      return formObj;
    }

    return body;
  }

  /**
   * Inject welcome properties into request body, preserving original format.
   * Handles FormData, URL-encoded strings, JSON strings, and plain objects.
   */
  function injectWelcomeProperties(opts) {
    var body = opts.body;
    var newOpts = Object.assign({}, opts);

    if (body instanceof FormData) {
      body.append('properties[' + config.props.item + ']', 'true');
      body.append('properties[' + config.props.type + ']', config.product.type);
      newOpts.body = body;
      return newOpts;
    }

    if (typeof body === 'string') {
      try {
        var jsonBody = JSON.parse(body);
        if (!jsonBody.properties) jsonBody.properties = {};
        jsonBody.properties[config.props.item] = 'true';
        jsonBody.properties[config.props.type] = config.product.type;
        newOpts.body = JSON.stringify(jsonBody);
        return newOpts;
      } catch (e) {
        var params = new URLSearchParams(body);
        params.set('properties[' + config.props.item + ']', 'true');
        params.set('properties[' + config.props.type + ']', config.product.type);
        newOpts.body = params.toString();
        return newOpts;
      }
    }

    // Plain object — convert to JSON as last resort
    if (typeof body === 'object' && body !== null) {
      if (!body.properties) body.properties = {};
      body.properties[config.props.item] = 'true';
      body.properties[config.props.type] = config.product.type;
      newOpts.body = JSON.stringify(body);
      newOpts.headers = Object.assign({}, opts.headers || {}, {
        'Content-Type': 'application/json'
      });
      return newOpts;
    }

    return newOpts;
  }

  /**
   * Create a blocked response mimicking Shopify's error format,
   * then force cart UI to re-sync so the drawer/page stays accurate.
   */
  function blockedResponse(msg) {
    var response = new Response(
      JSON.stringify({ status: 422, message: msg, description: msg }),
      {
        status: 422,
        statusText: 'Unprocessable Entity',
        headers: { 'Content-Type': 'application/json' }
      }
    );

    // Dispatch after the current promise chain resolves so the caller
    // has finished processing the response before the UI refreshes.
    Promise.resolve().then(function () {
      document.body.dispatchEvent(new CustomEvent('baseline:modalcart:afteradditem'));
    });

    return response;
  }

  /**
   * Extract URL string from fetch arguments (handles string, URL, and Request)
   */
  function resolveURL(input) {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    if (input instanceof Request) return input.url;
    return String(input);
  }

  /**
   * Post-add cart validation: catches welcome items added outside PDP context
   * (e.g., quick-add buttons, apps, direct API calls).
   * If multiple welcome items are found, removes the most recently added one.
   */
  function validateCartPostAdd() {
    setTimeout(function () {
      getCart().then(function (cart) {
        if (!cart || !cart.items) return;

        var welcomeItems = [];
        for (var i = 0; i < cart.items.length; i++) {
          if (cart.items[i].properties && cart.items[i].properties[config.props.item]) {
            welcomeItems.push(cart.items[i]);
          }
        }

        if (welcomeItems.length > 1) {
          var toRemove = welcomeItems[welcomeItems.length - 1];
          originalFetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: toRemove.key, quantity: 0 })
          }).then(function () {
            showError(config.msg.limit);
            updateCartUI();
          });
        }

        // Sync welcome keys cache
        welcomeKeysInCart = {};
        for (var j = 0; j < welcomeItems.length; j++) {
          welcomeKeysInCart[welcomeItems[j].key] = true;
        }
      });
    }, 300);
  }

  /**
   * Intercept fetch requests to enforce cart restrictions.
   * All /cart/add and /cart/change calls are intercepted regardless of page context.
   */
  window.fetch = function (url, opts) {
    opts = opts || {};
    var urlString = resolveURL(url);

    if (urlString.indexOf('/cart/add') !== -1) {
      return handleCartAdd(url, opts);
    }

    if (urlString.indexOf('/cart/change') !== -1) {
      return handleCartChange(url, opts);
    }

    return originalFetch(url, opts);
  };

  /**
   * Handle add-to-cart: block if welcome item already in cart,
   * inject welcome properties preserving original body format.
   * On non-welcome pages, runs post-add validation as a safety net.
   */
  function handleCartAdd(url, opts) {
    return getCart().then(function (cart) {
      if (!cart) return originalFetch(url, opts);

      var existing = findWelcomeInCart(cart);

      if (config.product.isWelcome) {
        // On a welcome product page — enforce limits and inject properties
        if (existing) {
          if (existing.type && config.product.type && existing.type !== config.product.type) {
            showError(config.msg.mix);
            return blockedResponse(config.msg.mix);
          }
          showError(config.msg.limit);
          return blockedResponse(config.msg.limit);
        }

        var newOpts = injectWelcomeProperties(opts);

        return originalFetch(url, newOpts).then(function (res) {
          if (res.ok) {
            res.clone().json().then(function (data) {
              if (data.key) {
                if (!welcomeKeysInCart) welcomeKeysInCart = {};
                welcomeKeysInCart[data.key] = true;
              }
            }).catch(function (err) { console.error('Cart update failed:', err); });
            // Store collection type for Continue Shopping link and footer swap
            if (config.product.type) {
              sessionStorage.setItem('welcome_collection_type', config.product.type);
            }
          }
          return res;
        });
      }

      // Not on a welcome product page — allow add, then validate cart
      return originalFetch(url, opts).then(function (res) {
        if (res.ok) validateCartPostAdd();
        return res;
      });
    });
  }

  /**
   * Handle cart change: block quantity increases for welcome items
   */
  function handleCartChange(url, opts) {
    var body = parseBody(opts.body);
    if (!body) return originalFetch(url, opts);

    var itemKey = body.id || body.line;
    var newQty = parseInt(body.quantity, 10);

    // Skip check if quantity is 0, 1, or not a number (remove or no increase)
    if (isNaN(newQty) || newQty <= 1) return originalFetch(url, opts);

    // Skip check only if we've confirmed no welcome items in cart
    if (welcomeKeysInCart !== null && Object.keys(welcomeKeysInCart).length === 0) {
      return originalFetch(url, opts);
    }

    // Only fetch cart if we know there's a welcome item present
    return getCart().then(function (cart) {
      if (!cart) return originalFetch(url, opts);

      var item = null;

      if (/^\d+$/.test(String(itemKey))) {
        var lineIdx = parseInt(itemKey, 10) - 1;
        item = cart.items[lineIdx];
      } else {
        for (var i = 0; i < cart.items.length; i++) {
          if (cart.items[i].key === itemKey) {
            item = cart.items[i];
            break;
          }
        }
      }

      if (item && item.properties && item.properties[config.props.item]) {
        showError(config.msg.qty);
        return blockedResponse(config.msg.qty);
      }

      return originalFetch(url, opts);
    });
  }

  /**
   * Update "Continue Shopping" link on empty cart to point to welcome collection
   */
  function updateContinueShoppingLink() {
    var type = sessionStorage.getItem('welcome_collection_type');
    if (!type) return;

    var links = document.querySelectorAll('#main-cart-items .supports-cookies a[href="/"]');
    links.forEach(function (link) {
      if (type === 'aviation') {
        link.href = '/collections/siriusxm-aviation-welcome-store';
      } else if (type === 'marine') {
        link.href = '/collections/siriusxm-marine-welcome-store';
      }
    });
  }

  /**
   * Update cart drawer/page UI to disable controls for welcome items
   */
  function updateCartUI() {
    getCart().then(function (cart) {
      if (!cart || !cart.items) return;

      // Sync welcome keys cache
      welcomeKeysInCart = {};
      for (var j = 0; j < cart.items.length; j++) {
        if (cart.items[j].properties && cart.items[j].properties[config.props.item]) {
          welcomeKeysInCart[cart.items[j].key] = true;
        }
      }

      var hasWelcome = Object.keys(welcomeKeysInCart).length > 0;

      // Update Continue Shopping link when cart is empty
      if (cart.items.length === 0) {
        updateContinueShoppingLink();
      }

      // Update cart row UI for welcome items
      document.querySelectorAll('[data-cart-row]').forEach(function (row) {
        var key = row.getAttribute('data-cart-item-key');
        if (!key) return;

        var item = null;
        for (var i = 0; i < cart.items.length; i++) {
          if (cart.items[i].key === key) {
            item = cart.items[i];
            break;
          }
        }

        if (!item || !item.properties || !item.properties[config.props.item]) return;

        var plusBtn = row.querySelector('[data-qty-adjust="plus"]');
        var qtyInput = row.querySelector('[data-qty-input]');

        if (plusBtn) {
          plusBtn.disabled = true;
          plusBtn.style.opacity = '0.5';
          plusBtn.style.cursor = 'not-allowed';
        }

        if (qtyInput) {
          qtyInput.setAttribute('readonly', 'true');
          qtyInput.setAttribute('max', '1');
        }

        if (!row.querySelector('.welcome-badge')) {
          var badge = document.createElement('span');
          badge.className = 'welcome-badge';
          badge.style.cssText = 'display:inline-block;font-size:11px;padding:2px 8px;background:#f0f0f0;border-radius:3px;margin-top:4px;color:#666';
          badge.textContent = config.msg.badge;
          var link = row.querySelector('a[href*="/products/"]');
          if (link && link.parentNode) {
            link.parentNode.insertBefore(badge, link.nextSibling);
          }
        }
      });

      // Hide footer refund link when cart has welcome items
      var footerRefundLinks = document.querySelectorAll('.footer-refund-link');
      footerRefundLinks.forEach(function (link) {
        link.style.display = hasWelcome ? 'none' : '';
      });

      // Toggle consent label: hide Return Policy version, show welcome version
      var termsDefault = document.querySelector('.cart-terms-default');
      var termsWelcome = document.querySelector('.cart-terms-welcome');
      if (termsDefault) termsDefault.style.display = hasWelcome ? 'none' : '';
      if (termsWelcome) termsWelcome.style.display = hasWelcome ? '' : 'none';
    });
  }

  /**
   * Observe cart drawer and cart page for DOM changes to re-apply UI restrictions
   */
  var observer = new MutationObserver(function () {
    clearTimeout(uiTimeout);
    uiTimeout = setTimeout(updateCartUI, 150);
  });

  ['CartContainer', 'main-cart-items'].forEach(function (id) {
    var el = document.getElementById(id);
    if (el) observer.observe(el, { childList: true, subtree: true });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      updateCartUI();
      updateContinueShoppingLink();
    });
  } else {
    updateCartUI();
    updateContinueShoppingLink();
  }
})();
